import { useCallback, useMemo, useState } from 'react'
import {
    getAvailableCategoryTabs,
    getCategoryCounters,
    getCategoryOrderedImages,
    getImageCategoryTab,
    getImagesForCategoryTab,
    groupImagesByCategory,
} from '../category-tabs'
import { CategoryBrowseTab } from '../types'
import type { CategoryTab, CategoryTabOption, Image, SlideCounter } from '../types'

interface UseCategoryFilteredGalleryParams {
    images: Image[]
    video?: Image
    enableCategorizedGallery: boolean
    setFullScreen: (isOpen: boolean) => void
    onFullscreenImageClick?: (imageIndex: number) => void
}

interface UseCategoryFilteredGalleryReturn {
    activeCategoryTab: CategoryTab
    setActiveCategoryTab: (tab: CategoryTab) => void
    closeFullScreen: (isOpen: boolean) => void
    availableTabs: CategoryTabOption[]
    fullScreenImages: Image[]
    fullScreenVideo?: Image
    overlayImages: Image[]
    overlayVideo?: Image
    getOverlayIndex: (image: Image) => number
    syncActiveTabFromOverlayIndex: (overlayIndex: number) => void
    getOverlayCounter: (overlayIndex: number) => SlideCounter | null
    overlayIndexFromRawIndex: (rawIndex: number) => number
    rawIndexFromOverlayIndex: (overlayIndex: number) => number
    handleFullscreenImageClick: (index: number) => void
}

export const useCategoryFilteredGallery = ({
    images,
    video,
    enableCategorizedGallery,
    setFullScreen,
    onFullscreenImageClick,
}: UseCategoryFilteredGalleryParams): UseCategoryFilteredGalleryReturn => {
    const [activeCategoryTab, setActiveCategoryTab] = useState<CategoryTab>(CategoryBrowseTab.ALL)

    const closeFullScreen = useCallback(
        (isOpen: boolean) => {
            setFullScreen(isOpen)
            if (!isOpen) {
                setActiveCategoryTab(CategoryBrowseTab.ALL)
            }
        },
        [setFullScreen],
    )

    const imagesByCategory = useMemo(() => groupImagesByCategory(images), [images])

    const availableTabs = useMemo(
        () => (enableCategorizedGallery ? getAvailableCategoryTabs(imagesByCategory, video) : []),
        [enableCategorizedGallery, imagesByCategory, video],
    )

    const { images: fullScreenImages, video: fullScreenVideo } = useMemo(
        () =>
            enableCategorizedGallery ? getImagesForCategoryTab(images, video, activeCategoryTab) : { images, video },
        [enableCategorizedGallery, images, video, activeCategoryTab],
    )

    const overlayImages = useMemo(
        () => (enableCategorizedGallery ? getCategoryOrderedImages(imagesByCategory) : images),
        [enableCategorizedGallery, imagesByCategory, images],
    )

    const overlayVideo = video

    const overlayIndexByImage = useMemo(
        () => new Map<Image, number>(overlayImages.map((image, index) => [image, video ? index + 1 : index])),
        [overlayImages, video],
    )

    const getOverlayIndex = useCallback((image: Image) => overlayIndexByImage.get(image) ?? 0, [overlayIndexByImage])

    const getImageForOverlayIndex = useCallback(
        (overlayIndex: number): Image | undefined => {
            if (video && overlayIndex === 0) {
                return undefined
            }
            return overlayImages[video ? overlayIndex - 1 : overlayIndex]
        },
        [overlayImages, video],
    )

    const syncActiveTabFromOverlayIndex = useCallback(
        (overlayIndex: number) => {
            if (!enableCategorizedGallery) {
                return
            }
            setActiveCategoryTab(currentTab => {
                if (currentTab === CategoryBrowseTab.ALL) {
                    return currentTab
                }
                const image = getImageForOverlayIndex(overlayIndex)
                return image ? getImageCategoryTab(image) : CategoryBrowseTab.VIDEO
            })
        },
        [enableCategorizedGallery, getImageForOverlayIndex],
    )

    const categoryCounterByImage = useMemo(() => getCategoryCounters(imagesByCategory), [imagesByCategory])

    const getOverlayCounter = useCallback(
        (overlayIndex: number): SlideCounter | null => {
            if (!enableCategorizedGallery) {
                return null
            }
            if (activeCategoryTab === CategoryBrowseTab.ALL) {
                return { current: overlayIndex + 1, total: overlayImages.length + (video ? 1 : 0) }
            }
            const image = getImageForOverlayIndex(overlayIndex)
            // The video is its own single-item category.
            if (!image) {
                return { current: 1, total: 1 }
            }
            return categoryCounterByImage.get(image) ?? null
        },
        [
            overlayImages,
            video,
            enableCategorizedGallery,
            activeCategoryTab,
            getImageForOverlayIndex,
            categoryCounterByImage,
        ],
    )

    const getImageForRawIndex = useCallback(
        (rawIndex: number): Image | undefined => {
            if (video && rawIndex === 0) {
                return undefined
            }
            return images[video ? rawIndex - 1 : rawIndex]
        },
        [images, video],
    )

    const overlayIndexFromRawIndex = useCallback(
        (rawIndex: number) => {
            const image = getImageForRawIndex(rawIndex)
            return image ? getOverlayIndex(image) : 0
        },
        [getImageForRawIndex, getOverlayIndex],
    )

    const rawIndexByImage = useMemo(() => new Map(images.map((image, index) => [image, index])), [images])

    const rawIndexFromOverlayIndex = useCallback(
        (overlayIndex: number) => {
            const image = getImageForOverlayIndex(overlayIndex)
            if (!image) {
                return 0
            }
            const rawArrayIndex = rawIndexByImage.get(image) ?? 0
            return video ? rawArrayIndex + 1 : rawArrayIndex
        },
        [getImageForOverlayIndex, rawIndexByImage, video],
    )

    const handleFullscreenImageClick = useCallback(
        (overlayIndex: number) => {
            if (!onFullscreenImageClick) {
                return
            }
            const clickedImage = getImageForOverlayIndex(overlayIndex)
            const originalIndex = clickedImage && rawIndexByImage.get(clickedImage)
            onFullscreenImageClick(originalIndex ?? overlayIndex)
        },
        [onFullscreenImageClick, getImageForOverlayIndex, rawIndexByImage],
    )

    return {
        activeCategoryTab,
        setActiveCategoryTab,
        closeFullScreen,
        availableTabs,
        fullScreenImages,
        fullScreenVideo,
        overlayImages,
        overlayVideo,
        getOverlayIndex,
        syncActiveTabFromOverlayIndex,
        getOverlayCounter,
        overlayIndexFromRawIndex,
        rawIndexFromOverlayIndex,
        handleFullscreenImageClick,
    }
}
